import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {CreativeCardScratchpadState} from '../../scratchpad/creativeCardScratchpad.types';
import getOrCreateCreativeCard from '../../utils/creative/getOrCreateCreativeCard';
import setCreativeTool from './setCreativeTool';
import setIsFallbackTool from './setIsFallbackTool';
import getCreativesTool from '../../../promptToCardAgent/tools/creatives/getCreativesTool';

const cardSpecificInstructions = `1. Use the ${V2CardAgentToolName.SetCreative} tool to set the creative. If a creative ID is provided, use it directly. Only use the getCreatives tool if no ID is provided or if the user provided other search details.
2. Use the ${V2CardAgentToolName.SetIsFallback} tool to mark the card as a fallback creative when requested. Fallback creatives cannot be connected to other cards and must not use products, location, content, or weather. Only non-attached cards can be set as fallback.
3. Landing page and UTM parameters cannot be changed; if the user asks, refuse and say it is not supported.

### Creative card specific instructions
- When setting a creative ID, the creative is validated for campaign compatibility. If the creative is not available, explain the error to the user.
- Fallback creative: If the user wants a fallback creative, use ${V2CardAgentToolName.SetIsFallback} with isFallback=true. The card must not receive inputs from other cards.`;

export default function addOrUpdateCreativeCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, CreativeCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId: cardId} = preProcessStandaloneAgent(params, store);

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateCreativeCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.CREATIVE]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.CREATIVE,
        cardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),
        getCreativesTool(store),
        setCreativeTool(store),
        setIsFallbackTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card: await getOrCreateCreativeCard(cardId, store)};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateCreativeCard,
    description:
      'Add or update a Creative card. Allow to set creative and define creative as fallback.',
    displayName: 'Working on creative card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
